import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FiPhone, FiMapPin, FiZap, FiTrendingUp } from 'react-icons/fi';

const PerformanceUpgrades = () => {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen">
      <Helmet>
        <title>Performance Upgrades Dallas | Tuning, Exhaust, Turbos | Paradise-Performance</title>
        <meta name="description" content="Dallas performance upgrades & tuning. Cold air intakes, exhaust systems, ECU tuning, turbos. More power, better sound. Real gains, not ricer mods. Call (817) 382-2316." />
        <link rel="canonical" href="https://paradise-performance.com/services/performance-upgrades" />
      </Helmet>

      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-black to-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img 
            src="/assets/assets/images/new/transmission-repair/transmission-software-performance-upgrade.webp"
            alt="Performance tuning and upgrades"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <h1 className="text-5xl font-bold mb-6">Performance Upgrades Dallas</h1>
          <p className="text-xl mb-8 max-w-3xl">
            Want real power, not Instagram clout? We build performance that works on the street, 
            not just the dyno. From mild to wild, we make your ride faster—properly.
          </p>
          <a href="tel:469-928-8011" className="inline-block bg-red-600 text-white px-8 py-4 rounded-lg font-bold hover:bg-red-700">
            <FiZap className="inline mr-2" />Let's Build Power: (817) 382-2316
          </a>
        </div>
      </section>

      {/* Reality Check */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold mb-8">Performance That Actually Performs</h2>
          <p className="text-lg text-gray-700 mb-6">
            Sick of "performance" shops selling you overpriced air filters that add 2hp? 
            We're talking real gains—20%, 30%, even 50% more power when done right. 
            Not stickers and LED strips.
          </p>
          <div className="grid md:grid-cols-3 gap-6 text-center">
            <div>
              <div className="text-4xl font-bold text-red-600">+87hp</div>
              <p className="text-gray-600">Average turbo upgrade gain</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-red-600">+35%</div>
              <p className="text-gray-600">Typical tune improvement</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-red-600">-1.2s</div>
              <p className="text-gray-600">0-60 improvement average</p>
            </div>
          </div>
        </div>
      </section>

      {/* Image Gallery */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            <div className="relative h-64 overflow-hidden rounded-lg shadow-xl">
              <img 
                src="/assets/assets/images/new/euro-repair-img/bmw-turbo-leak.webp"
                alt="Turbo upgrade service"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                <p className="text-white font-bold text-sm">Turbo System Upgrades</p>
              </div>
            </div>
            <div className="relative h-64 overflow-hidden rounded-lg shadow-xl">
              <img 
                src="/assets/assets/images/new/euro-repair-img/porsche-brake-hd.webp"
                alt="Performance brake upgrades"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                <p className="text-white font-bold text-sm">High Performance Brakes</p>
              </div>
            </div>
            <div className="relative h-64 overflow-hidden rounded-lg shadow-xl">
              <img 
                src="/assets/assets/images/new/suspension/dallas-suspension-repair-client-Paradise-performance5.webp"
                alt="Suspension performance upgrades"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                <p className="text-white font-bold text-sm">Sport Suspension Systems</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold mb-8 text-center">Real Performance Mods</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <FiTrendingUp className="w-12 h-12 text-red-600 mb-4" />
              <h3 className="text-xl font-bold mb-2">ECU Tuning</h3>
              <p className="text-gray-700 mb-2">
                Custom dyno tuning for your setup. Not canned tunes. 
                Real gains: 20-40hp naturally aspirated, 50-100hp turbocharged.
              </p>
              <p className="font-bold text-green-600">$599-1,299</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <FiTrendingUp className="w-12 h-12 text-red-600 mb-4" />
              <h3 className="text-xl font-bold mb-2">Forced Induction</h3>
              <p className="text-gray-700 mb-2">
                Turbo kits, superchargers, supporting mods. 
                Turn your grocery getter into a weapon.
              </p>
              <p className="font-bold text-green-600">$3,500-8,000</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <FiTrendingUp className="w-12 h-12 text-red-600 mb-4" />
              <h3 className="text-xl font-bold mb-2">Exhaust Systems</h3>
              <p className="text-gray-700 mb-2">
                Headers, high-flow cats, cat-backs. Sound good, 
                perform better. No rice, all nice.
              </p>
              <p className="font-bold text-green-600">$800-2,500</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <FiTrendingUp className="w-12 h-12 text-red-600 mb-4" />
              <h3 className="text-xl font-bold mb-2">Intake & Fuel</h3>
              <p className="text-gray-700 mb-2">
                Cold air intakes, bigger injectors, fuel pumps. 
                Feed the beast properly.
              </p>
              <p className="font-bold text-green-600">$300-1,500</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-black text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready for Real Speed?</h2>
          <p className="text-xl mb-8">Stop dreaming, start building.</p>
          <a href="tel:469-928-8011" className="inline-block bg-red-600 text-white px-10 py-5 rounded-lg font-bold text-lg hover:bg-red-700">
            <FiPhone className="inline mr-2" /> (817) 382-2316
          </a>
          <p className="mt-6"><FiMapPin className="inline mr-2" />305 Co Rd 3470
Paradise, TX 76073</p>
        </div>
      </section>
    </motion.div>
  );
};

export default PerformanceUpgrades;
